import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';

import type {
  CodeSearchResult,
  CodeType,
  EvidenceSpanRef,
  RelatedCodeItem
} from '../../interface/codes';
import { ADD_CODE_DIALOG as D, INK, INK_MUTED, SURFACE_WARM, TX_LABEL_COLOR } from '../../styles/tokens';
import { AtomButton } from './AtomButton';
import { CodeSearchField } from './CodeSearchField';
import { CodeTypeChips } from './CodeTypeChips';
import { EvidenceSpanSection } from './EvidenceSpanSection';
import { RelatedCodesList } from './RelatedCodesList';

export type AddCodeSelection = {
  readonly codeType: CodeType;
  readonly code: string;
  readonly description: string;
};

export type AddCodeModalProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly finalFocusRef?: React.RefObject<HTMLElement>;
  readonly initialCodeType?: CodeType;
  readonly onSearch?: (codeType: CodeType, term: string) => Promise<CodeSearchResult[]>;
  readonly relatedCodes?: readonly RelatedCodeItem[];
  readonly evidenceSpans?: readonly EvidenceSpanRef[];
  readonly activeNoteLabel?: string;
  readonly onAddCode?: (selection: AddCodeSelection) => Promise<void>;
};

export const AddCodeModal: React.FC<AddCodeModalProps> = ({
  isOpen,
  onClose,
  finalFocusRef,
  initialCodeType = 'ICD',
  onSearch,
  relatedCodes = [],
  evidenceSpans,
  activeNoteLabel,
  onAddCode
}) => {
  const [codeType, setCodeType] = useState<CodeType>(initialCodeType);
  const [selection, setSelection] = useState<AddCodeSelection | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openKey, setOpenKey] = useState<CodeType | null>(isOpen ? initialCodeType : null);

  const session = useRef(0);

  const nextOpenKey = isOpen ? initialCodeType : null;
  if (openKey !== nextOpenKey) {
    setOpenKey(nextOpenKey);
    if (nextOpenKey !== null) {
      setCodeType(nextOpenKey);
      setSelection(null);
      setError(null);
      setSubmitting(false);
    }
  }

  useEffect(() => {
    session.current += 1;
  }, [isOpen, initialCodeType]);

  const changeType = (next: CodeType): void => {
    setCodeType(next);
    setSelection(null);
    setError(null);
  };
  const select = (code: { code: string; description: string }): void => {
    setSelection({ codeType, code: code.code, description: code.description });
    setError(null);
  };
  const submit = async (): Promise<void> => {
    if (!selection || !onAddCode) return;
    const mine = session.current;
    setSubmitting(true);
    setError(null);
    try {
      await onAddCode(selection);
      if (mine !== session.current) return;
      onClose();
    } catch {
      if (mine !== session.current) return;
      setError("Couldn't add the code. Try again.");
    } finally {
      if (mine === session.current) setSubmitting(false);
    }
  };

  const hint = !selection
    ? 'Select a code above'
    : onAddCode
    ? `${selection.code} — ${selection.description}`
    : `${selection.code} · Adding a code is not available yet`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} finalFocusRef={finalFocusRef} isCentered>
      <ModalOverlay />
      <ModalContent aria-label="Add code" maxW={D.width} borderRadius={D.radius} boxShadow={D.shadow}
        sx={{ '& .chakra-modal__body': { padding: `${D.bodyPadY} ${D.padX} !important` } }}>
        <Flex data-testid="add-code-header" align="center" gap="14px" px={D.padX} py={D.headerPadY}
          bg={SURFACE_WARM} borderTopRadius={D.radius} borderBottomWidth="1px" borderBottomColor={D.rule}>
          <Flex align="center" gap="6px" fontSize="15px" fontWeight={700} color={INK}>
            <Text as="span">+</Text>
            <Text as="span">Add code</Text>
          </Flex>
          <CodeTypeChips value={codeType} onChange={changeType} />
          <ModalCloseButton position="static" w="23px" h="22px" color={TX_LABEL_COLOR}
            sx={{ '& svg': { width: '10px', height: '10px' } }} />
        </Flex>
        <ModalBody data-testid="add-code-content">
          <CodeSearchField key={codeType} codeType={codeType} onSearch={onSearch} onSelect={select} />
          {codeType === 'ICD' && <RelatedCodesList codes={relatedCodes} onSelect={select} />}
          <EvidenceSpanSection spans={evidenceSpans} activeNoteLabel={activeNoteLabel} />
        </ModalBody>
        <ModalFooter data-testid="add-code-footer" px={D.padX} py={D.headerPadY} gap="8px" bg={SURFACE_WARM}
          borderBottomRadius={D.radius} borderTopWidth="1px" borderTopColor={D.rule}>
          <Text data-testid="add-code-hint" mr="auto" fontSize="12px" fontWeight={500} color={INK_MUTED}>
            {hint}
          </Text>
          {error && (
            <Text role="alert" fontSize="12px" color="red.600">
              {error}
            </Text>
          )}
          <AtomButton className="btn-secondary" onClick={onClose}>
            Cancel
          </AtomButton>
          <AtomButton className="btn-primary" isLoading={submitting} onClick={submit}
            disabled={!selection || !onAddCode || submitting}>
            Add code
          </AtomButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
